/**
 * Wishlist functionality for Shopify theme
 * Handles wishlist storage and UI updates for both guests and logged-in customers
 */

const WISHLIST_STRINGS = window.wishlistStrings || {
  add: 'Add to wishlist',
  remove: 'Remove from wishlist'
};

class Wishlist {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.isCustomer = typeof window.Shopify?.customer !== 'undefined' && window.Shopify.customer && window.Shopify.customer.id;
    this.customerId = window.Shopify?.customer?.id || null;
    this.init();
  }

  normalizeItem(item) {
    if (!item) return null;

    if (typeof item === 'string' || typeof item === 'number') {
      const value = String(item).trim();
      if (!value) return null;
      if (/^\d+$/.test(value)) {
        return { handle: null, id: value };
      }
      return { handle: value, id: null };
    }

    if (typeof item === 'object') {
      const handle = item.handle ? String(item.handle).trim() : null;
      let id = item.id != null ? String(item.id).trim() : null;

      if (id) {
        const match = id.match(/(\d+)$/);
        if (match) {
          id = match[1];
        }
      }

      if (!handle && !id) return null;

      return {
        handle: handle || null,
        id: id || null
      };
    }

    return null;
  }

  itemsEqual(a, b) {
    if (!a || !b) return false;
    if (a.handle && b.handle && a.handle === b.handle) return true;
    if (a.id && b.id && a.id === b.id) return true;
    return false;
  }

  uniqueItems(items) {
    if (!Array.isArray(items)) return [];
    const unique = [];

    items.forEach((item) => {
      const normalized = this.normalizeItem(item);
      if (!normalized) return;

      if (!unique.some((existing) => this.itemsEqual(existing, normalized))) {
        unique.push(normalized);
      }
    });

    return unique;
  }

  init() {
    this.syncOnLogin();
    this.updateAllButtons();
    this.updateHeaderCount();
    document.addEventListener('customer:login', () => {
      this.syncToAccount();
    });
  }

  getItems() {
    if (this.isCustomer && this.customerId) {
      return this.uniqueItems(this.getCustomerWishlist());
    }

    try {
      const stored = localStorage.getItem(this.storageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      return this.uniqueItems(parsed);
    } catch (e) {
      return [];
    }
  }

  saveItems(items, options = {}) {
    const unique = this.uniqueItems(items);

    if (this.isCustomer && this.customerId) {
      this.saveToCustomerAccount(unique);
    } else {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(unique));
      } catch (e) {
        console.error('Failed to save wishlist to localStorage:', e);
      }
    }

    if (!options.silent) {
      this.dispatchEvent();
    }
  }

  add(productId) {
    const normalized = this.normalizeItem(productId);
    if (!normalized) return;

    const items = this.getItems();
    if (!items.some((item) => this.itemsEqual(item, normalized))) {
      items.push(normalized);
      this.saveItems(items);
    }
  }

  remove(productId) {
    const normalized = this.normalizeItem(productId);
    if (!normalized) return;

    const items = this.getItems();
    const filtered = items.filter((item) => !this.itemsEqual(item, normalized));
    this.saveItems(filtered);
  }

  toggle(product) {
    const normalized = this.normalizeItem(product);
    if (!normalized) return false;

    const items = this.getItems();
    const index = items.findIndex((item) => this.itemsEqual(item, normalized));
    if (index > -1) {
      items.splice(index, 1);
      this.saveItems(items);
      return false;
    } else {
      items.push(normalized);
      this.saveItems(items);
      return true;
    }
  }

  hasItem(productId, itemsList = null) {
    const normalized = this.normalizeItem(productId);
    if (!normalized) return false;
    const items = Array.isArray(itemsList) ? itemsList : this.getItems();
    return items.some((item) => this.itemsEqual(item, normalized));
  }

  getCount() {
    return this.getItems().length;
  }

  updateAllButtons(preloadedItems) {
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    const items = Array.isArray(preloadedItems) ? preloadedItems : this.getItems();

    buttons.forEach(button => {
      const productHandle = button.dataset.productHandle ? button.dataset.productHandle.trim() : null;
      const productId = button.dataset.productId ? String(button.dataset.productId).trim() : null;
      if (!productHandle && !productId) return;

      const isActive = this.hasItem({ handle: productHandle, id: productId }, items);
      button.classList.toggle('wishlist-button--active', isActive);
      button.setAttribute('aria-pressed', isActive);
      button.setAttribute('aria-label', isActive ? WISHLIST_STRINGS.remove : WISHLIST_STRINGS.add);
      button.setAttribute('title', isActive ? WISHLIST_STRINGS.remove : WISHLIST_STRINGS.add);
    });
  }

  updateHeaderCount(precomputedCount) {
    const count = typeof precomputedCount === 'number' ? precomputedCount : this.getCount();
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    
    countElements.forEach(element => {
      element.textContent = count;
      element.classList.toggle('visually-hidden', count === 0);
      element.setAttribute('data-count', count);
    });
  }

  getCustomerWishlist() {
    try {
      const wishlistData = window.Shopify?.customer?.wishlist;
      if (wishlistData && typeof wishlistData === 'string') {
        return JSON.parse(wishlistData);
      }
      return wishlistData || [];
    } catch (e) {
      return [];
    }
  }

  saveToCustomerAccount(items) {
    // Note: This requires a backend solution to persist to Shopify customer metafields
    // For now, we'll store in localStorage as a fallback
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }

  syncOnLogin() {
    if (this.isCustomer && this.customerId) {
      const customerWishlist = this.uniqueItems(this.getCustomerWishlist());
      const localWishlist = (() => {
        try {
          const stored = localStorage.getItem(this.storageKey);
          const parsed = stored ? JSON.parse(stored) : [];
          return this.uniqueItems(parsed);
        } catch (e) {
          return [];
        }
      })();

      const merged = this.uniqueItems([...customerWishlist, ...localWishlist]);
      if (merged.length > 0) {
        this.saveItems(merged);
      }
    }
  }

  syncToAccount() {
    // This would sync to customer account via API
    // For now, just update the UI
    this.updateAllButtons();
    this.updateHeaderCount();
  }

  dispatchEvent() {
    const items = this.getItems();
    const count = items.length;

    this.updateAllButtons(items);
    this.updateHeaderCount(count);

    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { items, count }
    }));
  }
}

// Auto-initialize if script is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.wishlist) {
      window.wishlist = new Wishlist();
    }
    initWishlistButtons();
    observeWishlistButtons();
  });
} else {
  if (!window.wishlist) {
    window.wishlist = new Wishlist();
  }
  initWishlistButtons();
  observeWishlistButtons();
}

/**
 * Observe DOM for new wishlist buttons and attach handlers
 */
function observeWishlistButtons() {
  if (window._wishlistObserver) {
    window._wishlistObserver.disconnect();
  }
  
  window._wishlistObserver = new MutationObserver((mutations) => {
    let shouldAttach = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.matches && node.matches('[data-wishlist-button]')) {
            shouldAttach = true;
          } else if (node.querySelector && node.querySelector('[data-wishlist-button]')) {
            shouldAttach = true;
          }
        }
      });
    });
    
    if (shouldAttach) {
      attachDirectHandlers();
    }
  });
  
  window._wishlistObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

// Export for use in other scripts
window.Wishlist = Wishlist;

/**
 * Initialize wishlist button event handlers
 * Uses a clean, reliable approach with proper event delegation
 */
function initWishlistButtons() {
  // Remove any existing listeners to prevent duplicates
  if (window._wishlistClickHandler) {
    document.removeEventListener('click', window._wishlistClickHandler, true);
    document.removeEventListener('touchend', window._wishlistClickHandler, true);
  }
  
  /**
   * Main event handler - uses capture phase to intercept before card links
   * This handler checks if the click target is a wishlist button or inside one
   */
  window._wishlistClickHandler = function(e) {
    // Find the closest wishlist button from the click target
    const button = e.target.closest('[data-wishlist-button]');
    if (!button || !window.wishlist) return;
    
    const productHandle = button.dataset.productHandle ? button.dataset.productHandle.trim() : null;
    const productId = button.dataset.productId ? String(button.dataset.productId).trim() : null;
    if (!productHandle && !productId) return;
    
    // CRITICAL: Stop all event propagation immediately
    // This prevents any parent elements (like card links) from receiving the event
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    // Add animation class
    button.classList.add('wishlist-button--animating');
    button.setAttribute('aria-busy', 'true');
    
    // Toggle wishlist
    const wasAdded = window.wishlist.toggle({ handle: productHandle, id: productId });
    
    // Update button state
    setTimeout(() => {
      button.classList.remove('wishlist-button--animating');
      button.setAttribute('aria-busy', 'false');
      button.classList.toggle('wishlist-button--active', wasAdded);
      button.setAttribute('aria-pressed', wasAdded);
      button.setAttribute('aria-label', wasAdded ? WISHLIST_STRINGS.remove : WISHLIST_STRINGS.add);
      button.setAttribute('title', wasAdded ? WISHLIST_STRINGS.remove : WISHLIST_STRINGS.add);
      
      // Update icon
      const icon = button.querySelector('.wishlist-icon');
      if (icon) {
        icon.classList.toggle('wishlist-icon--active', wasAdded);
      }
    }, 300);
  };
  
  // Attach handler in capture phase (runs before bubbling phase) for both click and touch
  document.addEventListener('click', window._wishlistClickHandler, true);
  document.addEventListener('touchend', window._wishlistClickHandler, true);
  
  // Also attach directly to existing buttons as backup
  attachDirectHandlers();
  
  // Re-attach handlers with delays to catch any timing issues
  setTimeout(() => {
    attachDirectHandlers();
  }, 100);
  
  setTimeout(() => {
    attachDirectHandlers();
  }, 500);
  
  setTimeout(() => {
    attachDirectHandlers();
  }, 1000);
  
  // Update buttons when wishlist changes
  document.addEventListener('wishlist:updated', () => {
    if (window.wishlist) {
      window.wishlist.updateAllButtons();
      window.wishlist.updateHeaderCount();
    }
    // Re-attach handlers for any new buttons
    attachDirectHandlers();
  });
}

/**
 * Attach click handlers directly to wishlist buttons as backup
 * This ensures buttons work even if event delegation fails
 */
function attachDirectHandlers() {
  const buttons = document.querySelectorAll('[data-wishlist-button]');
  buttons.forEach(button => {
    // Remove any existing handler
    if (button._wishlistHandler) {
      button.removeEventListener('click', button._wishlistHandler, true);
      button.removeEventListener('touchend', button._wishlistHandler, true);
    }
    
    // Create new handler - must be a named function for proper removal
    button._wishlistHandler = function(e) {
      // Verify this is the correct button
      const clickedButton = e.target.closest('[data-wishlist-button]');
      if (clickedButton !== button) return;
      
      if (!window.wishlist) return;
      
      const productHandle = button.dataset.productHandle ? button.dataset.productHandle.trim() : null;
      const productId = button.dataset.productId ? String(button.dataset.productId).trim() : null;
      if (!productHandle && !productId) return;
      
      // CRITICAL: Stop all event propagation immediately
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      // Add animation class
      button.classList.add('wishlist-button--animating');
      button.setAttribute('aria-busy', 'true');
      
      // Toggle wishlist
      const wasAdded = window.wishlist.toggle({ handle: productHandle, id: productId });
      
      // Update button state
      setTimeout(() => {
        button.classList.remove('wishlist-button--animating');
        button.setAttribute('aria-busy', 'false');
        button.classList.toggle('wishlist-button--active', wasAdded);
        button.setAttribute('aria-pressed', wasAdded);
        button.setAttribute('aria-label', wasAdded ? WISHLIST_STRINGS.remove : WISHLIST_STRINGS.add);
        button.setAttribute('title', wasAdded ? WISHLIST_STRINGS.remove : WISHLIST_STRINGS.add);
        
        // Update icon
        const icon = button.querySelector('.wishlist-icon');
        if (icon) {
          icon.classList.toggle('wishlist-icon--active', wasAdded);
        }
      }, 300);
    };
    
    // Attach in capture phase for both click and touch events
    button.addEventListener('click', button._wishlistHandler, true);
    button.addEventListener('touchend', button._wishlistHandler, true);
  });
}
